import React, { useMemo } from 'react';
import FontCard from './FontCard.jsx';
import { useVirtualList } from '../hooks/useVirtualList.js';
import { useFontContext } from '../context/FontContext.jsx';
import { useDebouncedValue } from '../hooks/useDebouncedValue.js';

const BASE_ROW_HEIGHT = 96;
const ROW_HEIGHT_PADDING = 64;

/**
 * Renders the (potentially 1000+ item) font manifest as a virtualized,
 * vertically-scrolling specimen list. Only rows intersecting the
 * viewport (plus overscan) ever mount a FontCard, so scroll performance
 * stays flat regardless of library size.
 */
export default function FontList({ fonts }) {
  const { fontSize } = useFontContext();

  // Row height scales with the preview font size so large specimens don't
  // get clipped. We debounce it so dragging the size slider doesn't force
  // a full virtualization recalculation on every intermediate frame.
  const debouncedFontSize = useDebouncedValue(fontSize, 80);

  const itemHeight = useMemo(
    () => Math.max(BASE_ROW_HEIGHT, debouncedFontSize + ROW_HEIGHT_PADDING),
    [debouncedFontSize]
  );

  const { containerRef, virtualItems, totalHeight } = useVirtualList({
    itemCount: fonts.length,
    itemHeight,
    overscan: 6,
  });

  if (fonts.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 py-24 text-center">
        <div className="max-w-sm">
          <p className="font-display text-lg">No fonts found yet</p>
          <p className="mt-2 font-sans text-sm text-ink/60 dark:text-paper/60">
            Drop .ttf, .woff, or .woff2 files into{' '}
            <code className="font-mono">/public/fonts</code> and refresh to see
            them appear here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="specimen-scroll flex-1 overflow-y-auto"
      role="list"
      aria-label="Font specimens"
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        {virtualItems.map(({ index, offsetTop }) => {
          const font = fonts[index];
          return (
            <div
              key={font.id}
              role="listitem"
              style={{
                position: 'absolute',
                top: offsetTop,
                left: 0,
                right: 0,
                height: itemHeight,
              }}
            >
              <FontCard font={font} height={itemHeight} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
